import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { Helmet } from 'react-helmet-async';

export const FAQ = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  const faqs = [
    {
      question: 'What exactly is Searchefy?',
      answer:
        'Searchefy is a backlink campaign management platform. It gives you a curated directory of 500+ high-authority websites where you can build backlinks, plus an unlimited project tracker to manage outreach and monitor whether each link was submitted, published, or rejected — all in one dashboard.',
    },
    {
      question: 'Why does this cost only $9?',
      answer:
        "Because it's a founder-built tool that started as a personal workflow solution. We'd rather have a large base of happy users than an artificially inflated price. As the directory grows and features expand, the price will likely increase — but early users are locked in at $9 forever.",
    },
    {
      question: "What's included in the $9?",
      answer:
        'Everything. 500+ curated backlink sources, unlimited projects, custom link tracking, status management, analytics dashboard, and all future updates and directory additions. Pay once. Use forever.',
    },
    {
      question: 'Is this a one-time payment or a subscription?',
      answer:
        'One-time. You pay $9 once and have access forever. No monthly or annual charges. Ever.',
    },
    {
      question: 'Who is Searchefy for?',
      answer:
        'SEO specialists, content marketers, marketing heads, agency owners, and founders who want to build backlinks systematically without spreadsheet chaos or expensive monthly tool subscriptions.',
    },
    {
      question: 'How is Searchefy different from Ahrefs or Semrush?',
      answer:
        'Those tools tell you about backlinks — who links to whom, analysis, audits. Searchefy is where you execute — find curated sites, track your campaigns, measure your results. They complement each other. Searchefy is your operational layer.',
    },
    {
      question: 'Do I need SEO experience to use it?',
      answer:
        'No. The directory is pre-curated and filtered. Just pick a category and DA threshold, select sites, add them to a project, and start. No deep SEO knowledge needed.',
    },
    {
      question: 'Will the directory keep getting updated?',
      answer:
        'Yes. We continuously add new high-quality sites and remove low-quality or penalized ones. As a lifetime member, every update is included automatically.',
    },
    {
      question: 'Can I add my own backlink sources?',
      answer:
        "Yes. You can add custom links to any project — not just sources from the directory. Track anything you're working on.",
    },
    {
      question: 'What happens after I pay?',
      answer:
        "After payment, you'll be redirected to create your Searchefy account using the email you paid with. Your account is activated instantly. Full access begins immediately.",
    },
    {
      question: 'Is there a refund policy?',
      answer:
        "All sales are final. Given the $9 price point and the immediate value of the directory and tracker, we do not offer refunds. If you have any issues, reach out to us at thepratishjain@gmail.com and we'll make it right.",
    },
    {
      question: 'Can I use Searchefy for multiple websites?',
      answer:
        'Yes. You get unlimited projects — use it across as many websites, clients, or campaigns as you want.',
    },
  ];

  // FAQ Schema for SEO
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <section
        id="faq"
        data-testid="faq-section"
        className="section-light py-24 md:py-32 bg-white"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-4">
              Frequently Asked{' '}
              <span className="gradient-text">Questions</span>
            </h2>
            <p className="text-gray-600">
              Everything you need to know about Searchefy.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border border-gray-200 rounded-xl px-6 bg-white"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left font-medium text-gray-900 hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default FAQ;
